import clsx from 'clsx';
import React from 'react';
import styles from './index.module.scss';
import { YesNoOptionsSelector } from 'components/ChipOptionsSelector';
import ButtonWithArrowIcon from 'components/Buttons/ButtonWithArrowIcon';
import * as yup from 'yup';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { TextButton } from 'components/Buttons/TextButton';

export interface YesNoQuestionForm {
  secondaryQuestionType?: 'yesNo' | 'explanation';
  mainQuestionAnwser?: boolean;
  secondaryYesNoAnwser?: boolean;
  secondaryExplanationAnwser?: string;
}

const validationSchema = yup.object({
  mainQuestionAnwser: yup.boolean().required(),
  secondaryQuestionType: yup.string().notRequired(),
  secondaryYesNoAnwser: yup
    .boolean()
    .when(['mainQuestionAnwser', 'secondaryQuestionType'], (fields, schema) => {
      return fields.length && fields[0] && fields[1] === 'yesNo'
        ? schema.required()
        : schema.notRequired();
    }),
  secondaryExplanationAnwser: yup
    .mixed()
    .when(['mainQuestionAnwser', 'secondaryQuestionType'], (fields, schema) => {
      return fields.length && fields[0] && fields[1] === 'explanation'
        ? schema.required()
        : schema.notRequired();
    }),
});

interface YesNoQuestionProps {
  title: string;
  question: string;
  mainQuestionAnswer?: boolean;
  onNext: (answer: boolean, secondAnswer?: any) => void;
  secondaryQuestion?: {
    question: string;
    type: 'yesNo' | 'explanation';
  };
  className?: string;
}

const YesNoQuestion: React.FC<YesNoQuestionProps> = (props) => {
  const navigate = useNavigate();
  const formik = useFormik<YesNoQuestionForm>({
    validationSchema,
    initialValues: {
      mainQuestionAnwser: props?.mainQuestionAnswer || undefined,
      secondaryYesNoAnwser: undefined,
      secondaryExplanationAnwser: undefined,
      secondaryQuestionType: props.secondaryQuestion?.type,
    } as YesNoQuestionForm,
    onSubmit: async (values: any) => {
      const secondaryAnswer =
        values.mainQuestionAnwser === true
          ? props.secondaryQuestion?.type === 'yesNo'
            ? values.secondaryYesNoAnwser
            : values.secondaryExplanationAnwser
          : null;
      props.onNext(values.mainQuestionAnwser, secondaryAnswer);
    },
  });

  const handleNext = () => {
    formik.submitForm();
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className={clsx(styles.root, props.className)}>
      <h2>{props.title}</h2>
      <p className={clsx(styles.questionText, styles.mainQuestionText)}>
        {props.question}
      </p>

      <YesNoOptionsSelector
        className={styles.mainYesNoAnwser}
        onChange={(isSelected: boolean) => {
          formik.setValues({
            ...formik.values,
            mainQuestionAnwser: isSelected,
            secondaryYesNoAnwser: undefined,
            secondaryExplanationAnwser: undefined,
          });
        }}
      />

      {formik.values.mainQuestionAnwser && props.secondaryQuestion && (
        <>
          <p className={styles.questionText}>
            {props.secondaryQuestion.question}
          </p>
          {props.secondaryQuestion?.type === 'yesNo' && (
            <YesNoOptionsSelector
              className={styles.mainYesNoAnwser}
              onChange={(isChecked: boolean) => {
                formik.setFieldValue('secondaryYesNoAnwser', isChecked);
              }}
            />
          )}
          {props.secondaryQuestion?.type === 'explanation' && (
            <input
              type="text"
              name="secondaryExplanationAnwser"
              onChange={formik.handleChange}
              autoComplete="off"
              className={styles.explanationInput}
              placeholder="Explanation"
            />
          )}
        </>
      )}

      <div>
        <ButtonWithArrowIcon
          disabled={!formik.isValid || !formik.dirty}
          onClick={handleNext}
          variant="contained"
          color="primary"
        >
          Next
        </ButtonWithArrowIcon>
      </div>
      <div>
        <TextButton onClick={handleBack}>Go Back</TextButton>
      </div>
    </div>
  );
};

export default YesNoQuestion;
